/**
 * Draggable Event Cards Grid
 * Provides drag functionality for horizontal scrolling event cards
 */
class DraggableEventGrid {
  constructor(container) {
    this.container = container;
    this.isDown = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.velocity = 0;
    this.lastX = 0;
    this.lastTime = 0;
    
    this.init();
  }
  
  init() {
    this.addEventListeners();
  }
  
  addEventListeners() {
    // Mouse events
    this.container.addEventListener('mousedown', this.handleMouseDown.bind(this));
    this.container.addEventListener('mouseleave', this.handleMouseUp.bind(this));
    this.container.addEventListener('mouseup', this.handleMouseUp.bind(this));
    this.container.addEventListener('mousemove', this.handleMouseMove.bind(this));
    
    // Touch events
    this.container.addEventListener('touchstart', this.handleTouchStart.bind(this), { passive: false });
    this.container.addEventListener('touchend', this.handleTouchEnd.bind(this), { passive: false });
    this.container.addEventListener('touchmove', this.handleTouchMove.bind(this), { passive: false });
    
    // Prevent context menu on long press
    this.container.addEventListener('contextmenu', (e) => e.preventDefault());
  }
  
  isClickableElement(element) {
    // Check if the element or any of its parents is a clickable element
    let currentElement = element;
    while (currentElement && currentElement !== this.container) {
      // Check for common clickable elements
      if (currentElement.tagName === 'A' || 
          currentElement.tagName === 'BUTTON' || 
          currentElement.type === 'button' ||
          currentElement.classList.contains('button') ||
          currentElement.classList.contains('event-card__link') ||
          currentElement.classList.contains('event-card__play-btn') ||
          currentElement.classList.contains('event-card__photos-btn') ||
          currentElement.classList.contains('slideshow-controls__arrow') ||
          currentElement.classList.contains('slideshow-controls__arrow--previous') ||
          currentElement.classList.contains('slideshow-controls__arrow--next') ||
          currentElement.onclick ||
          currentElement.getAttribute('onclick') ||
          currentElement.getAttribute('href') ||
          currentElement.getAttribute('data-action') ||
          currentElement.getAttribute('data-toggle') ||
          currentElement.getAttribute('data-target')) {
        return true;
      }
      currentElement = currentElement.parentElement;
    }
    return false;
  }
  
  handleMouseDown(e) {
    // Check if the target is a clickable element
    if (this.isClickableElement(e.target)) {
      return; // Don't start dragging if clicking on a link or button
    }
    
    this.isDown = true;
    this.startX = e.pageX - this.container.offsetLeft;
    this.scrollLeft = this.container.scrollLeft;
    this.lastX = e.pageX;
    this.lastTime = Date.now();
    this.hasMoved = false; // Track if mouse has moved significantly
  }
  
  handleMouseUp() {
    this.isDown = false;
    this.container.classList.remove('grabbing');
  }
  
  handleMouseMove(e) {
    if (!this.isDown) return;
    
    const x = e.pageX - this.container.offsetLeft;
    const walk = (x - this.startX) * 2;
    
    // Only start dragging if mouse has moved more than 5 pixels
    if (Math.abs(walk) > 5) {
      this.hasMoved = true;
      this.container.classList.add('grabbing');
      e.preventDefault();
      
      this.container.scrollLeft = this.scrollLeft - walk;
      
      // Calculate velocity for momentum
      const currentTime = Date.now();
      const deltaTime = currentTime - this.lastTime;
      if (deltaTime > 0) {
        this.velocity = (e.pageX - this.lastX) / deltaTime;
      }
      this.lastX = e.pageX;
      this.lastTime = currentTime;
    }
  }
  
  handleTouchStart(e) {
    // Check if the target is a clickable element
    if (this.isClickableElement(e.target)) {
      // Allow the click to proceed normally
      return;
    }
    
    this.isDown = true;
    this.startX = e.touches[0].pageX - this.container.offsetLeft;
    this.scrollLeft = this.container.scrollLeft;
    this.lastX = e.touches[0].pageX;
    this.lastTime = Date.now();
    this.hasMoved = false; // Track if touch has moved significantly
  }
  
  handleTouchEnd() {
    this.isDown = false;
    this.container.classList.remove('grabbing');
    
    // Apply momentum scrolling
    if (Math.abs(this.velocity) > 0.1) {
      this.applyMomentum();
    }
  }
  
  handleTouchMove(e) {
    if (!this.isDown) return;
    
    // Check if we're touching a clickable element
    if (this.isClickableElement(e.target)) {
      return; // Don't interfere with clickable elements
    }
    
    const x = e.touches[0].pageX - this.container.offsetLeft;
    const walk = (x - this.startX) * 2;
    
    // Only start dragging if touch has moved more than 5 pixels
    if (Math.abs(walk) > 5) {
      this.hasMoved = true;
      this.container.classList.add('grabbing');
      e.preventDefault();
      
      this.container.scrollLeft = this.scrollLeft - walk;
      
      // Calculate velocity
      const currentTime = Date.now();
      const deltaTime = currentTime - this.lastTime;
      if (deltaTime > 0) {
        this.velocity = (e.touches[0].pageX - this.lastX) / deltaTime;
      }
      this.lastX = e.touches[0].pageX;
      this.lastTime = currentTime;
    }
  }
  
  applyMomentum() {
    const friction = 0.95;
    const minVelocity = 0.01;
    
    const animate = () => {
      if (Math.abs(this.velocity) < minVelocity) return;
      
      this.container.scrollLeft -= this.velocity * 10;
      this.velocity *= friction;
      
      requestAnimationFrame(animate);
    };
    
    requestAnimationFrame(animate);
  }
}

// Initialize draggable grids when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.event-cards-grid').forEach(grid => {
    new DraggableEventGrid(grid);
  });
});

// Re-initialize when new sections are loaded (for theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {HTMLElement} */ (event.target);
  if (target) {
    target.querySelectorAll('.event-cards-grid').forEach((grid) => {
      new DraggableEventGrid(grid);
    });
  }
});

// Clean up when sections are unloaded
document.addEventListener('shopify:section:unload', (event) => {
  // Cleanup is handled automatically by removing event listeners
  // when the DOM element is removed
});

/**
 * Equalize heights of all event-card-body elements
 * Finds the maximum height among all event-card-body divs and sets all to that height
 */
function equalizeEventCardBodyHeights() {
  // Find all event-card-body elements within event-cards-grid containers
  const eventCardsGrids = document.querySelectorAll('.event-cards-grid');
  
  eventCardsGrids.forEach((grid) => {
    const cardBodies = grid.querySelectorAll('.event-card-body');
    
    if (cardBodies.length === 0) return;
    
    // Reset heights to auto to get natural heights
    cardBodies.forEach((body) => {
      const bodyElement = /** @type {HTMLElement} */ (body);
      bodyElement.style.height = 'auto';
    });
    
    // Get all heights and find the maximum
    let maxHeight = 0;
    cardBodies.forEach((body) => {
      const bodyElement = /** @type {HTMLElement} */ (body);
      const height = bodyElement.offsetHeight;
      if (height > maxHeight) {
        maxHeight = height;
      }
    });
    
    // Set all card bodies to the maximum height
    if (maxHeight > 0) {
      cardBodies.forEach((body) => {
        const bodyElement = /** @type {HTMLElement} */ (body);
        bodyElement.style.height = `${maxHeight}px`;
      });
    }
  });
}

// Equalize heights when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Wait a bit for images and content to load
  setTimeout(() => {
    equalizeEventCardBodyHeights();
  }, 100);
  
  // Also equalize after a longer delay to account for lazy-loaded images
  setTimeout(() => {
    equalizeEventCardBodyHeights();
  }, 500);
});

// Equalize heights on window resize
/** @type {ReturnType<typeof setTimeout> | null} */
let resizeTimeout = null;
window.addEventListener('resize', () => {
  if (resizeTimeout !== null) {
    clearTimeout(resizeTimeout);
  }
  resizeTimeout = setTimeout(() => {
    equalizeEventCardBodyHeights();
  }, 250);
});

// Re-equalize when new sections are loaded (for theme editor)
document.addEventListener('shopify:section:load', () => {
  setTimeout(() => {
    equalizeEventCardBodyHeights();
  }, 100);
});

// Make function globally available so it can be called after cards are dynamically added
window.equalizeEventCardBodyHeights = equalizeEventCardBodyHeights;
